import * as React from 'react';

import {
  AsyncBoundary,
  Button,
  Col,
  ContentCard,
  ContentSection,
  Row,
  Select,
  TextField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideSave, LucideTruck } from 'lucide-react';

import { purchaseOrderFreightQueryAtom } from '~/modules/trade/_graphql/queryPurchaseOrderFreight';
import { salesOrderFreightQueryAtom } from '~/modules/trade/_graphql/querySalesOrderFreight';
import {
  $createPurchaseOrderFreight,
  $createSalesOrderFreight,
  $updatePurchaseOrderFreight,
  $updateSalesOrderFreight,
} from '~/modules/trade/_state/contractFreight.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { AccountSelect } from '~/modules/transportation/components/@shared/AccountSelect';
import { FreightLaneSelect } from '~/modules/transportation/components/@shared/FreightLaneSelect';

type ContractFreightSectionProps = {
  entityType: 'purchaseOrder' | 'salesOrder';
  entityId: string;
  disabled?: boolean;
};

type FreightFormState = {
  carrierId: string;
  freightLaneId: string;
  rateFormula: string;
  chargebackFormula: string;
  fuelSurchargeFormula: string;
  fuelSurchargeMode: string;
  notes: string;
};

const emptyFormState: FreightFormState = {
  carrierId: '',
  freightLaneId: '',
  rateFormula: '',
  chargebackFormula: '',
  fuelSurchargeFormula: '',
  fuelSurchargeMode: '',
  notes: '',
};

export const ContractFreightSection = ({
  entityType,
  entityId,
  disabled = false,
}: ContractFreightSectionProps) => {
  const t = useTranslations();
  const isPurchaseOrder = entityType === 'purchaseOrder';

  const queryAtom = React.useMemo(
    () =>
      isPurchaseOrder
        ? purchaseOrderFreightQueryAtom(() => ({
            variables: { purchaseOrderId: entityId },
            skip: !entityId,
          }))
        : salesOrderFreightQueryAtom(() => ({
            variables: { salesOrderId: entityId },
            skip: !entityId,
          })),
    [isPurchaseOrder, entityId],
  );

  const {
    data: freightConfig,
    isLoading,
    error,
    refetch,
  } = useAtomValue(queryAtom);

  const createPurchaseOrderFreight = useSetAtom($createPurchaseOrderFreight);
  const updatePurchaseOrderFreight = useSetAtom($updatePurchaseOrderFreight);
  const createSalesOrderFreight = useSetAtom($createSalesOrderFreight);
  const updateSalesOrderFreight = useSetAtom($updateSalesOrderFreight);

  const [form, setForm] = React.useState<FreightFormState>(emptyFormState);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (freightConfig) {
      setForm({
        carrierId: freightConfig.carrierId ?? '',
        freightLaneId: freightConfig.freightLaneId ?? '',
        rateFormula: freightConfig.rateFormula ?? '',
        chargebackFormula: freightConfig.chargebackFormula ?? '',
        fuelSurchargeFormula: freightConfig.fuelSurchargeFormula ?? '',
        fuelSurchargeMode: freightConfig.fuelSurchargeMode ?? '',
        notes: freightConfig.notes ?? '',
      });
    }
  }, [freightConfig]);

  const handleFieldChange = React.useCallback(
    (field: keyof FreightFormState) => (value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSave = React.useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        carrierId: form.carrierId || null,
        freightLaneId: form.freightLaneId || null,
        rateFormula: form.rateFormula || null,
        chargebackFormula: form.chargebackFormula || null,
        fuelSurchargeFormula: form.fuelSurchargeFormula || null,
        fuelSurchargeMode: form.fuelSurchargeMode || null,
        notes: form.notes || null,
      };

      if (freightConfig?.id) {
        if (isPurchaseOrder) {
          await updatePurchaseOrderFreight({
            id: freightConfig.id,
            ...payload,
          });
        } else {
          await updateSalesOrderFreight({ id: freightConfig.id, ...payload });
        }
      } else {
        if (isPurchaseOrder) {
          await createPurchaseOrderFreight({
            purchaseOrderId: entityId,
            ...payload,
          });
        } else {
          await createSalesOrderFreight({
            salesOrderId: entityId,
            ...payload,
          });
        }
      }

      refetch?.();
    } finally {
      setIsSaving(false);
    }
  }, [
    form,
    freightConfig?.id,
    isPurchaseOrder,
    entityId,
    refetch,
    createPurchaseOrderFreight,
    updatePurchaseOrderFreight,
    createSalesOrderFreight,
    updateSalesOrderFreight,
  ]);

  return (
    <ContentCard className="@ContractFreightSection">
      <ContentSection
        variant="panel"
        title={
          <Row gap2 className="items-center">
            <LucideTruck size={18} />
            <span>{t.contractFreight.title}</span>
          </Row>
        }
      >
        <AsyncBoundary loading={isLoading} error={error}>
          <Col gap4>
            <div className="grid grid-cols-2 gap-4">
              <AccountSelect
                label={t.contractFreight.fields.carrier}
                value={form.carrierId || null}
                onChange={(account) =>
                  handleFieldChange('carrierId')(account?.id ?? '')
                }
                isCarrier
                isDisabled={disabled}
              />
              <FreightLaneSelect
                label={t.contractFreight.fields.freightLane}
                value={form.freightLaneId || null}
                onChange={(lane) =>
                  handleFieldChange('freightLaneId')(lane?.id ?? '')
                }
                isDisabled={disabled}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TextField
                value={form.rateFormula}
                label={t.contractFreight.fields.rateFormula}
                onChange={handleFieldChange('rateFormula')}
                placeholder={t.contractFreight.placeholders.rateFormula}
                isDisabled={disabled}
              />
              <TextField
                value={form.chargebackFormula}
                label={t.contractFreight.fields.chargebackFormula}
                onChange={handleFieldChange('chargebackFormula')}
                placeholder={t.contractFreight.placeholders.chargebackFormula}
                isDisabled={disabled}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TextField
                value={form.fuelSurchargeFormula}
                label={t.contractFreight.fields.fuelSurchargeFormula}
                onChange={handleFieldChange('fuelSurchargeFormula')}
                placeholder={
                  t.contractFreight.placeholders.fuelSurchargeFormula
                }
                isDisabled={disabled}
              />
              <Select
                value={form.fuelSurchargeMode || undefined}
                label={t.contractFreight.fields.fuelSurchargeMode}
                onChange={handleFieldChange('fuelSurchargeMode')}
                placeholder={t.contractFreight.placeholders.fuelSurchargeMode}
                options={[
                  {
                    key: 'PERCENTAGE',
                    label: t.contractFreight.fuelSurchargeModes.percentage,
                  },
                  {
                    key: 'FLAT',
                    label: t.contractFreight.fuelSurchargeModes.flat,
                  },
                  {
                    key: 'PER_MILE',
                    label: t.contractFreight.fuelSurchargeModes.perMile,
                  },
                ]}
                isDisabled={disabled}
              />
            </div>

            <TextField
              value={form.notes}
              label={t.contractFreight.fields.notes}
              onChange={handleFieldChange('notes')}
              placeholder={t.contractFreight.placeholders.notes}
              textArea
              isDisabled={disabled}
            />

            {!disabled && (
              <Row className="justify-end">
                <Button
                  variant="solid"
                  color="primary"
                  onPress={handleSave}
                  isDisabled={isSaving}
                  icon={<LucideSave size={16} />}
                >
                  {freightConfig?.id
                    ? t.contractFreight.buttons.update
                    : t.contractFreight.buttons.save}
                </Button>
              </Row>
            )}
          </Col>
        </AsyncBoundary>
      </ContentSection>
    </ContentCard>
  );
};
